import { DashboardFilter } from 'src/app/dashboards/shared/filters/dashboard_filter';
import {
  Analysis,
  DashboardConfigurationDependencies,
  Granularity,
} from 'src/app/dashboards/shared/dashboard-config/dashboard-config.model';
import {
  DashboardDistrictSingleSelectFilter,
  DashboardDistrictsMultiSelectFilter,
  DashboardRemoteDistrictSingleSelectFilter,
  DashboardRemoteStateSingleSelectFilter,
  DashboardRemoteSubMarketSingleSelectFilter,
  DashboardStateSingleSelectFilter,
  DashboardStatesMultiSelectFilter,
  DashboardSubMarketSingleSelectFilter,
  DashboardSubMarketsMultiSelectFilter,
  DashboardWzClassesMultiSelectFilter,
  DashboardWzClassSingleSelectFilter,
} from 'src/app/dashboards/shared/filters/dashboard_filter_implementations';

export const TIME_SERIES_BAR_SHELL_ID = 'time-series-bar' as const;

export type DashboardTimeSeriesBarShell =
  | 'states'
  | 'districts'
  | 'districtSubMarkets'
  | 'subMarkets'
  | 'wzClasses'
  | 'districtWzClasses'
  | 'wzClassStates'
  | 'tourism';

export interface DashboardTimeSeriesBarGranularityFilterOptions {
  statesDefaultValues?: string[];
  districtsDefaultValues?: string[];
  useRemoteSubMarket?: boolean;
}

export interface DashboardTimeSeriesBarShellProfile {
  id: typeof TIME_SERIES_BAR_SHELL_ID;
  granularityId: Granularity;
  analysisId: Analysis;
  /** Overrides the map's feature label column (district names on the Kreise map). */
  mapValueLabelKey?: 'district_name';
  /** Overrides the granularity's heading words (Tourism). */
  mapHeadingGranularity?: (filterValues: Map<string, any>) => string;
  chartHeadingGranularity?: (filterValues: Map<string, any>) => string;
  buildGranularityFilters: (
    deps: DashboardConfigurationDependencies,
    options?: DashboardTimeSeriesBarGranularityFilterOptions,
  ) => DashboardFilter<any>[];
}

export const dashboardTimeSeriesBarShellProfiles: Record<DashboardTimeSeriesBarShell, DashboardTimeSeriesBarShellProfile> = {
  states: {
    id: TIME_SERIES_BAR_SHELL_ID,
    granularityId: Granularity.States,
    analysisId: Analysis.TimeSeries,
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: true,
      }),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
        defaultValue: '9',
      }),
    ],
  },
  districts: {
    id: TIME_SERIES_BAR_SHELL_ID,
    granularityId: Granularity.Districts,
    analysisId: Analysis.TimeSeries,
    mapValueLabelKey: 'district_name',
    buildGranularityFilters: (deps, options) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: false,
        allInitiallySelected: !options?.statesDefaultValues?.length,
        defaultValues: options?.statesDefaultValues,
      }),
      new DashboardDistrictsMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelected: !options?.districtsDefaultValues?.length,
        defaultValues: options?.districtsDefaultValues,
      }),
      options?.useRemoteSubMarket
        ? new DashboardRemoteSubMarketSingleSelectFilter(
            deps.localDatasetsService,
            {},
          )
        : new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {}),
    ],
  },
  subMarkets: {
    id: TIME_SERIES_BAR_SHELL_ID,
    granularityId: Granularity.SubMarkets,
    analysisId: Analysis.TimeSeries,
    buildGranularityFilters: (deps) => [
      new DashboardStateSingleSelectFilter(deps.localDatasetsService, {
        addSumGermany: true,
      }),
      new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelectedExceptValue: deps.sumSubMarketIds,
      }),
    ],
  },
  districtSubMarkets: {
    id: TIME_SERIES_BAR_SHELL_ID,
    granularityId: Granularity.DistrictSubMarkets,
    analysisId: Analysis.TimeSeries,
    buildGranularityFilters: (deps) => [
      new DashboardRemoteStateSingleSelectFilter(deps.localDatasetsService, {
        addSumGermany: false,
      }),
      new DashboardDistrictSingleSelectFilter(deps.localDatasetsService, {
        autoDisplayFirst: true,
      }),
      new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelectedExceptValue: deps.sumSubMarketIds,
      }),
    ],
  },
  wzClasses: {
    id: TIME_SERIES_BAR_SHELL_ID,
    granularityId: Granularity.WzClasses,
    analysisId: Analysis.TimeSeries,
    buildGranularityFilters: (deps, options) => [
      new DashboardStateSingleSelectFilter(deps.localDatasetsService, {
        addSumGermany: true,
      }),
      options?.useRemoteSubMarket
        ? new DashboardRemoteSubMarketSingleSelectFilter(
            deps.localDatasetsService,
            {},
          )
        : new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {}),
      new DashboardWzClassesMultiSelectFilter(deps.localDatasetsService, {}),
    ],
  },
  districtWzClasses: {
    id: TIME_SERIES_BAR_SHELL_ID,
    granularityId: Granularity.DistrictWzClasses,
    analysisId: Analysis.TimeSeries,
    buildGranularityFilters: (deps, options) => [
      new DashboardRemoteDistrictSingleSelectFilter(deps.localDatasetsService, {
        autoDisplayFirst: true,
      }),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
        excluded: deps.sumSubMarketIds,
      }),
      new DashboardWzClassesMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelected: true,
      }),
    ],
  },
  wzClassStates: {
    id: TIME_SERIES_BAR_SHELL_ID,
    granularityId: Granularity.WzClassStates,
    analysisId: Analysis.TimeSeries,
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: true,
      }),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
        defaultValue: '9',
      }),
      new DashboardWzClassSingleSelectFilter(deps.localDatasetsService, {}),
    ],
  },
  tourism: {
    id: TIME_SERIES_BAR_SHELL_ID,
    granularityId: Granularity.Unknown,
    analysisId: Analysis.TimeSeries,
    mapHeadingGranularity: () => 'IN ACCOMMODATION',
    chartHeadingGranularity: () => 'IN ACCOMMODATION NACH BUNDESLÄNDERN',
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: true,
        allInitiallySelected: false,
        defaultValues: ['1'],
      }),
    ],
  },
};
